import { useEffect, useRef } from "react";
import {
  AreaSeries,
  BarSeries,
  BaselineSeries,
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  LineStyle,
  createChart,
  type IChartApi,
  type ISeriesApi,
  type MouseEventParams,
  type SeriesDefinition,
  type SeriesType,
  type UTCTimestamp,
} from "lightweight-charts";

import type { Candle, ChartResponse, HybridResult } from "../api/types";
import type { ChartType } from "../lib/chartTypes";
import type { ResolvedTheme } from "../theme/ThemeProvider";
import {
  calculateBollingerBands,
  calculateIndicatorData,
  getIndicatorDefinition,
  type IndicatorKey,
  type LinePoint,
} from "../lib/indicators";

type AnySeries = ISeriesApi<SeriesType>;

const SERIES_DEFS: Record<ChartType, SeriesDefinition<SeriesType>> = {
  candles: CandlestickSeries,
  bars: BarSeries,
  line: LineSeries,
  area: AreaSeries,
  baseline: BaselineSeries,
};

const UP = "#13795b";
const DOWN = "#b42318";

const toTime = (time: number) => time as UTCTimestamp;

const volumeFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

function formatNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: Math.abs(value) >= 100 ? 2 : 4,
  }).format(value);
}

function formatLegendDate(timeSec: number, interval: string): string {
  const date = new Date(timeSec * 1000);
  const intraday = /(m|h)$/.test(interval) && !/(d|wk|mo)/.test(interval);
  return new Intl.DateTimeFormat(
    "en-US",
    intraday
      ? { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
      : { year: "numeric", month: "short", day: "numeric" },
  ).format(date);
}

function cssColor(variableName: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

function chartThemeOptions() {
  return {
    layout: {
      background: { type: ColorType.Solid, color: cssColor("--chart-bg") },
      textColor: cssColor("--chart-text"),
      fontFamily: "Inter, Avenir Next, Segoe UI, Arial, sans-serif",
    },
    grid: {
      vertLines: { color: cssColor("--chart-grid") },
      horzLines: { color: cssColor("--chart-grid") },
    },
  };
}

function seriesOptions(type: ChartType) {
  switch (type) {
    case "candles":
      return {
        upColor: UP,
        downColor: DOWN,
        borderVisible: false,
        wickUpColor: UP,
        wickDownColor: DOWN,
        priceLineColor: "#1a5cff",
      };
    case "bars":
      return { upColor: UP, downColor: DOWN, thinBars: false, priceLineColor: "#1a5cff" };
    case "line":
      return { color: "#1a5cff", lineWidth: 2, priceLineColor: "#1a5cff" };
    case "area":
      return {
        lineColor: "#1a5cff",
        topColor: "rgba(26, 92, 255, 0.26)",
        bottomColor: "rgba(26, 92, 255, 0.02)",
        lineWidth: 2,
        priceLineColor: "#1a5cff",
      };
    case "baseline":
      return {
        baseValue: { type: "price", price: 0 },
        topLineColor: UP,
        topFillColor1: "rgba(19, 121, 91, 0.24)",
        topFillColor2: "rgba(19, 121, 91, 0.04)",
        bottomLineColor: DOWN,
        bottomFillColor1: "rgba(180, 35, 24, 0.04)",
        bottomFillColor2: "rgba(180, 35, 24, 0.24)",
        lineWidth: 2,
        priceLineColor: "#1a5cff",
      };
  }
}

function buildSeriesData(candles: Candle[], chartType: ChartType) {
  if (chartType === "candles" || chartType === "bars") {
    return candles.map((candle) => ({
      time: toTime(candle.time),
      open: candle.open,
      high: candle.high,
      low: candle.low,
      close: candle.close,
    }));
  }

  return candles
    .map((candle) => ({ time: toTime(candle.time), value: Number(candle.close) }))
    .filter((point) => Number.isFinite(point.value));
}

interface LegendEntry {
  candle: Candle;
  prevClose: number | null;
}

interface PriceChartProps {
  chart: ChartResponse | null;
  chartType: ChartType;
  activeIndicators: IndicatorKey[];
  agentResult: HybridResult | null;
  resolvedTheme: ResolvedTheme;
  resetNonce: number;
  isLoading?: boolean;
}

export function PriceChart({
  chart,
  chartType,
  activeIndicators,
  agentResult,
  resolvedTheme,
  resetNonce,
  isLoading = false,
}: PriceChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<AnySeries | null>(null);
  const volumeSeriesRef = useRef<ISeriesApi<"Histogram"> | null>(null);
  const indicatorSeriesRef = useRef<Record<string, AnySeries[]>>({});
  const priceLinesRef = useRef<ReturnType<AnySeries["createPriceLine"]>[]>([]);

  // Legend overlay state, updated imperatively on crosshair move to avoid React
  // re-renders on every pointer event.
  const legendRef = useRef<HTMLDivElement>(null);
  const legendDateRef = useRef<HTMLSpanElement>(null);
  const legendOpenRef = useRef<HTMLElement>(null);
  const legendHighRef = useRef<HTMLElement>(null);
  const legendLowRef = useRef<HTMLElement>(null);
  const legendCloseRef = useRef<HTMLElement>(null);
  const legendChangeRef = useRef<HTMLSpanElement>(null);
  const legendVolRef = useRef<HTMLElement>(null);

  const entriesRef = useRef<Map<number, LegendEntry>>(new Map());
  const lastEntryRef = useRef<LegendEntry | null>(null);
  const intervalRef = useRef<string>("");

  function renderLegend(entry: LegendEntry | null) {
    const legend = legendRef.current;
    if (!legend) {
      return;
    }
    if (!entry) {
      legend.classList.remove("is-visible", "is-up", "is-down");
      return;
    }
    const { candle, prevClose } = entry;
    legend.classList.add("is-visible");

    if (legendDateRef.current) {
      legendDateRef.current.textContent = formatLegendDate(candle.time, intervalRef.current);
    }
    if (legendOpenRef.current) legendOpenRef.current.textContent = formatNumber(candle.open);
    if (legendHighRef.current) legendHighRef.current.textContent = formatNumber(candle.high);
    if (legendLowRef.current) legendLowRef.current.textContent = formatNumber(candle.low);
    if (legendCloseRef.current) legendCloseRef.current.textContent = formatNumber(candle.close);
    if (legendVolRef.current) {
      legendVolRef.current.textContent = Number.isFinite(candle.volume)
        ? volumeFormatter.format(candle.volume)
        : "—";
    }

    const base = prevClose ?? candle.open;
    const diff = candle.close - base;
    const pct = base ? (diff / base) * 100 : 0;
    const sign = diff > 0 ? "+" : "";
    legend.classList.toggle("is-up", diff > 0);
    legend.classList.toggle("is-down", diff < 0);
    if (legendChangeRef.current) {
      legendChangeRef.current.textContent = `${sign}${formatNumber(diff)} (${sign}${pct.toFixed(2)}%)`;
    }
  }

  function handleCrosshair(param: MouseEventParams) {
    const time = param.time;
    if (typeof time === "number" && entriesRef.current.has(time)) {
      renderLegend(entriesRef.current.get(time) ?? null);
    } else {
      renderLegend(lastEntryRef.current);
    }
  }

  function clearPriceLines() {
    const series = seriesRef.current;
    if (!series) {
      return;
    }
    priceLinesRef.current.forEach((line) => {
      try {
        series.removePriceLine(line);
      } catch {
        // Price lines are best-effort visual overlays.
      }
    });
    priceLinesRef.current = [];
  }

  function clearIndicatorSeries() {
    const chartApi = chartRef.current;
    Object.values(indicatorSeriesRef.current).forEach((group) => {
      group.forEach((series) => {
        try {
          chartApi?.removeSeries(series);
        } catch {
          // Indicator overlays may already be gone after chart changes.
        }
      });
    });
    indicatorSeriesRef.current = {};
  }

  function addIndicatorLineSeries(color: string, lineWidth: 1 | 2): AnySeries {
    return chartRef.current!.addSeries(LineSeries, {
      color,
      lineWidth,
      priceLineVisible: false,
      lastValueVisible: true,
      crosshairMarkerVisible: true,
    });
  }

  function setLineData(series: AnySeries, points: LinePoint[]) {
    series.setData(points.map((point) => ({ time: toTime(point.time), value: point.value })));
  }

  function resetView() {
    const chartApi = chartRef.current;
    const series = seriesRef.current;
    if (!chartApi || !series) {
      return;
    }
    try {
      series.priceScale().applyOptions({ autoScale: true });
    } catch {
      // Autoscale reset is best-effort across Lightweight Charts versions.
    }
    try {
      chartApi.priceScale("right").applyOptions({ autoScale: true });
    } catch {
      // Older versions may only expose the series price scale.
    }
    try {
      chartApi.timeScale().fitContent();
    } catch {
      // If the time scale is unavailable, leave the chart as-is.
    }
  }

  // Create the chart + volume subpane once.
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const chartApi = createChart(containerRef.current, {
      autoSize: true,
      ...chartThemeOptions(),
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: { top: 0.08, bottom: 0.26 },
      },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false },
      crosshair: { mode: CrosshairMode.Normal },
    });
    chartRef.current = chartApi;

    const volumeSeries = chartApi.addSeries(HistogramSeries, {
      priceScaleId: "volume",
      priceFormat: { type: "volume" },
      priceLineVisible: false,
      lastValueVisible: false,
    });
    chartApi.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });
    volumeSeriesRef.current = volumeSeries;

    chartApi.subscribeCrosshairMove(handleCrosshair);

    return () => {
      chartApi.remove();
      chartRef.current = null;
      seriesRef.current = null;
      volumeSeriesRef.current = null;
      indicatorSeriesRef.current = {};
      priceLinesRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Re)create the main series whenever the chart type changes.
  useEffect(() => {
    const chartApi = chartRef.current;
    if (!chartApi) {
      return;
    }
    const series = chartApi.addSeries(SERIES_DEFS[chartType], seriesOptions(chartType));
    seriesRef.current = series;

    return () => {
      try {
        chartApi.removeSeries(series);
      } catch {
        // Removing an already-disposed series should not break the chart controls.
      }
      if (seriesRef.current === series) {
        seriesRef.current = null;
      }
      priceLinesRef.current = [];
    };
  }, [chartType]);

  // Render data + volume + indicators + price lines whenever inputs change.
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) {
      return;
    }

    clearPriceLines();

    if (!chart) {
      series.setData([]);
      volumeSeriesRef.current?.setData([]);
      clearIndicatorSeries();
      entriesRef.current = new Map();
      lastEntryRef.current = null;
      renderLegend(null);
      return;
    }

    const candles = chart.candles || [];
    intervalRef.current = chart.interval || "";
    const seriesData = buildSeriesData(candles, chartType);

    if (chartType === "baseline" && seriesData.length) {
      series.applyOptions({
        baseValue: { type: "price", price: (seriesData[0] as { value: number }).value },
      });
    }

    series.setData(seriesData);

    // Volume subpane.
    if (volumeSeriesRef.current) {
      volumeSeriesRef.current.setData(
        candles.map((candle) => ({
          time: toTime(candle.time),
          value: Number.isFinite(candle.volume) ? candle.volume : 0,
          color:
            candle.close >= candle.open ? "rgba(19,121,91,0.42)" : "rgba(180,35,24,0.42)",
        })),
      );
    }

    // Legend lookup map (time -> candle + previous close).
    const entries = new Map<number, LegendEntry>();
    let prevClose: number | null = null;
    candles.forEach((candle) => {
      entries.set(candle.time, { candle, prevClose });
      prevClose = candle.close;
    });
    entriesRef.current = entries;
    lastEntryRef.current = candles.length
      ? entries.get(candles[candles.length - 1].time) ?? null
      : null;
    renderLegend(lastEntryRef.current);

    // Indicators.
    clearIndicatorSeries();
    activeIndicators.forEach((key) => {
      const indicator = getIndicatorDefinition(key);
      if (!indicator) {
        return;
      }
      if (key === "bb20") {
        const bands = calculateBollingerBands(candles, 20, 2);
        const upper = addIndicatorLineSeries("#64748b", 1);
        const middle = addIndicatorLineSeries("#94a3b8", 1);
        const lower = addIndicatorLineSeries("#64748b", 1);
        setLineData(upper, bands.upper);
        setLineData(middle, bands.middle);
        setLineData(lower, bands.lower);
        indicatorSeriesRef.current[key] = [upper, middle, lower];
        return;
      }
      const lineSeries = addIndicatorLineSeries(indicator.color, 2);
      setLineData(lineSeries, calculateIndicatorData(key, candles));
      indicatorSeriesRef.current[key] = [lineSeries];
    });

    // Agent TP/SL price lines.
    if (agentResult) {
      const dashed = LineStyle.Dashed;
      (
        [
          ["TP", agentResult.take_profit, UP],
          ["SL", agentResult.stop_loss, DOWN],
        ] as const
      ).forEach(([title, rawPrice, color]) => {
        const price = Number(rawPrice);
        if (!Number.isFinite(price)) {
          return;
        }
        const line = series.createPriceLine({
          price,
          color,
          lineWidth: 2,
          lineStyle: dashed,
          axisLabelVisible: true,
          title,
        });
        priceLinesRef.current.push(line);
      });
    }

    resetView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, chartType, activeIndicators, agentResult]);

  // Reapply theme colors on light/dark change.
  useEffect(() => {
    chartRef.current?.applyOptions(chartThemeOptions());
  }, [resolvedTheme]);

  // Manual "Reset" button.
  useEffect(() => {
    if (resetNonce > 0) {
      resetView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetNonce]);

  return (
    <div className="chart-surface">
      <div className="chart-legend" ref={legendRef} aria-hidden>
        <span className="chart-legend-date" ref={legendDateRef} />
        <span className="chart-legend-ohlc">
          <span>
            <i>O</i>
            <b ref={legendOpenRef} />
          </span>
          <span>
            <i>H</i>
            <b ref={legendHighRef} />
          </span>
          <span>
            <i>L</i>
            <b ref={legendLowRef} />
          </span>
          <span>
            <i>C</i>
            <b ref={legendCloseRef} />
          </span>
        </span>
        <span className="chart-legend-meta">
          <span className="chart-legend-change" ref={legendChangeRef} />
          <span className="chart-legend-vol">
            Vol <b ref={legendVolRef} />
          </span>
        </span>
      </div>
      {isLoading && <div className="chart-loading" aria-hidden />}
      <div className="chart-canvas" ref={containerRef} />
    </div>
  );
}
